'use client';

import { c as _c } from "react/compiler-runtime";
import { BywayFavorite, CoffeeCup, MapPin } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { ListingCard } from '~/components/ListingCard';
import { Spinner } from '~/components/Spinner';
import { useCurrencyContext } from '~/contexts/Currencies';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import type { Accommodation } from '~/schemas/trip';
import { isBywayFavouriteAccommodation } from '~/utils/accommodationHelpers';
import { pricePerPerson } from '~/utils/paxHelpers/paxHelper';
import styles from './AlternativeAccommodationCard.module.css';
import { formatStationDistanceMeters } from './formatStationDistance';
export type AlternativeEntry = Accommodation & {
  priceDifference: {
    value: number;
    currencyId: string;
  };
  distanceFromArrivalStationMeters?: number | null;
  distanceFromDepartureStationMeters?: number | null;
};
type AlternativeAccommodationCardProps = {
  accommodation: AlternativeEntry;
  currentAccommodationId: number | undefined;
  adultCount: number;
  childAges: number[];
  updatingAccommodationId: number | null;
  layout?: 'vertical' | 'horizontal';
  onSelect: (accommodation: AlternativeEntry) => void;
};
export const AlternativeAccommodationCard = t0 => {
  const $ = _c(72);
  const {
    accommodation,
    currentAccommodationId,
    adultCount,
    childAges,
    updatingAccommodationId,
    layout: t1,
    onSelect
  } = t0;
  const layout = t1 === undefined ? "vertical" : t1;
  const t = useTranslations("journey");
  const commonT = useTranslations("common");
  const {
    convertPriceInGBP
  } = useCurrencyContext();
  const isTripUpdating = useIsTripUpdating();
  const isSelected = accommodation.id === currentAccommodationId;
  const isUpdating = updatingAccommodationId !== null;
  const isUpdatingThis = updatingAccommodationId === accommodation.id;
  let breakfast;
  let galleryImages;
  let isFavourite;
  let stationDistance;
  let t2;
  if ($[0] !== accommodation.distanceFromArrivalStationMeters || $[1] !== accommodation.distanceFromDepartureStationMeters || $[2] !== accommodation.images || $[3] !== accommodation.priceDifference.value || $[4] !== accommodation.ranking || $[5] !== accommodation.selectedRooms || $[6] !== accommodation.title || $[7] !== adultCount || $[8] !== childAges || $[9] !== convertPriceInGBP || $[10] !== isSelected || $[11] !== t) {
    const priceDifferencePerPerson = pricePerPerson(accommodation.priceDifference.value, adultCount, childAges);
    const priceDifferenceSign = priceDifferencePerPerson > 0 ? "+" : "";
    const priceLabel = t("accommodation_alternative_price_per_person", {
      amount: `${priceDifferenceSign}${convertPriceInGBP(priceDifferencePerPerson)}`
    });
    let t3;
    if ($[17] !== accommodation.selectedRooms) {
      t3 = accommodation.selectedRooms.some(_temp);
      $[17] = accommodation.selectedRooms;
      $[18] = t3;
    } else {
      t3 = $[18];
    }
    breakfast = t3;
    const arrivalMeters = accommodation.distanceFromArrivalStationMeters ?? 0;
    const departureMeters = accommodation.distanceFromDepartureStationMeters ?? 0;
    let t4;
    if ($[19] !== arrivalMeters || $[20] !== t) {
      t4 = arrivalMeters > 0 ? t("accommodation_distance_arrival", {
        distance: formatStationDistanceMeters(arrivalMeters)
      }) : null;
      $[19] = arrivalMeters;
      $[20] = t;
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    const arrivalDistance = t4;
    let t5;
    if ($[22] !== departureMeters || $[23] !== t) {
      t5 = departureMeters > 0 ? t("accommodation_distance_departure", {
        distance: formatStationDistanceMeters(departureMeters)
      }) : null;
      $[22] = departureMeters;
      $[23] = t;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    const departureDistance = t5;
    stationDistance = arrivalDistance ?? departureDistance;
    let t6;
    if ($[25] !== accommodation.ranking) {
      t6 = isBywayFavouriteAccommodation(accommodation.ranking);
      $[25] = accommodation.ranking;
      $[26] = t6;
    } else {
      t6 = $[26];
    }
    isFavourite = t6;
    let t7;
    if ($[27] !== accommodation.images || $[28] !== accommodation.title) {
      let t8;
      if ($[30] !== accommodation.title) {
        t8 = image => ({
          src: image.url,
          alt: accommodation.title
        });
        $[30] = accommodation.title;
        $[31] = t8;
      } else {
        t8 = $[31];
      }
      t7 = accommodation.images.map(t8);
      $[27] = accommodation.images;
      $[28] = accommodation.title;
      $[29] = t7;
    } else {
      t7 = $[29];
    }
    galleryImages = t7;
    t2 = isSelected ? t("selected") : t("accommodation_alternative_select_for", {
      price: priceLabel
    });
    $[0] = accommodation.distanceFromArrivalStationMeters;
    $[1] = accommodation.distanceFromDepartureStationMeters;
    $[2] = accommodation.images;
    $[3] = accommodation.priceDifference.value;
    $[4] = accommodation.ranking;
    $[5] = accommodation.selectedRooms;
    $[6] = accommodation.title;
    $[7] = adultCount;
    $[8] = childAges;
    $[9] = convertPriceInGBP;
    $[10] = isSelected;
    $[11] = t;
    $[12] = breakfast;
    $[13] = galleryImages;
    $[14] = isFavourite;
    $[15] = stationDistance;
    $[16] = t2;
  } else {
    breakfast = $[12];
    galleryImages = $[13];
    isFavourite = $[14];
    stationDistance = $[15];
    t2 = $[16];
  }
  const selectLabel = t2;
  let t3;
  if ($[32] !== galleryImages || $[33] !== isFavourite || $[34] !== t) {
    t3 = galleryImages.length > 0 ? <ListingCard.Gallery images={galleryImages} tag={isFavourite ? <ListingCard.Tag icon={<BywayFavorite decorative={true} size="small" />}>{t("byway_favourite")}</ListingCard.Tag> : undefined} /> : null;
    $[32] = galleryImages;
    $[33] = isFavourite;
    $[34] = t;
    $[35] = t3;
  } else {
    t3 = $[35];
  }
  let t4;
  if ($[36] !== accommodation.title) {
    t4 = <ListingCard.Title>{accommodation.title}</ListingCard.Title>;
    $[36] = accommodation.title;
    $[37] = t4;
  } else {
    t4 = $[37];
  }
  let t5;
  if ($[38] !== stationDistance) {
    t5 = stationDistance ? <li><MapPin decorative={true} size="small" />{stationDistance}</li> : null;
    $[38] = stationDistance;
    $[39] = t5;
  } else {
    t5 = $[39];
  }
  let t6;
  if ($[40] !== breakfast || $[41] !== t) {
    t6 = breakfast ? <li><CoffeeCup decorative={true} size="small" />{t("breakfast_included")}</li> : null;
    $[40] = breakfast;
    $[41] = t;
    $[42] = t6;
  } else {
    t6 = $[42];
  }
  let t7;
  if ($[43] !== t5 || $[44] !== t6) {
    t7 = <ul className={styles.cardList}>{t5}{t6}</ul>;
    $[43] = t5;
    $[44] = t6;
    $[45] = t7;
  } else {
    t7 = $[45];
  }
  let t8;
  if ($[46] !== accommodation.websiteUrl || $[47] !== commonT) {
    t8 = accommodation.websiteUrl ? <a className={styles.cardLink} href={accommodation.websiteUrl} target="_blank" rel="noopener noreferrer"><span className={styles.cardLinkLabel}>{commonT("see_more")}</span><span aria-hidden="true"> ↗</span></a> : null;
    $[46] = accommodation.websiteUrl;
    $[47] = commonT;
    $[48] = t8;
  } else {
    t8 = $[48];
  }
  let t9;
  if ($[49] !== t4 || $[50] !== t7 || $[51] !== t8) {
    t9 = <ListingCard.Content>{t4}{t7}{t8}</ListingCard.Content>;
    $[49] = t4;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  const t10 = layout === "horizontal" && styles.cardCtaHorizontal;
  let t11;
  if ($[53] !== t10) {
    t11 = clsx(styles.cardCta, t10);
    $[53] = t10;
    $[54] = t11;
  } else {
    t11 = $[54];
  }
  const t12 = isSelected || isUpdating || isTripUpdating;
  let t13;
  if ($[55] !== accommodation || $[56] !== onSelect) {
    t13 = () => onSelect(accommodation);
    $[55] = accommodation;
    $[56] = onSelect;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  let t14;
  if ($[58] !== isUpdatingThis || $[59] !== selectLabel) {
    t14 = isUpdatingThis ? <Spinner className={styles.cardCtaSpinner} /> : selectLabel;
    $[58] = isUpdatingThis;
    $[59] = selectLabel;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  let t15;
  if ($[61] !== isUpdatingThis || $[62] !== t11 || $[63] !== t12 || $[64] !== t13 || $[65] !== t14) {
    t15 = <ListingCard.Footer><button type="button" className={t11} disabled={t12} onClick={t13} aria-busy={isUpdatingThis}>{t14}</button></ListingCard.Footer>;
    $[61] = isUpdatingThis;
    $[62] = t11;
    $[63] = t12;
    $[64] = t13;
    $[65] = t14;
    $[66] = t15;
  } else {
    t15 = $[66];
  }
  let t16;
  if ($[67] !== layout || $[68] !== t15 || $[69] !== t3 || $[70] !== t9) {
    t16 = <ListingCard variant="accommodation" layout={layout} hasBackground={true} fullWidth={true}>{t3}{t9}{t15}</ListingCard>;
    $[67] = layout;
    $[68] = t15;
    $[69] = t3;
    $[70] = t9;
    $[71] = t16;
  } else {
    t16 = $[71];
  }
  return t16;
};
function _temp(room) {
  return room.boardBasis === "BB";
}